import React, { useState, memo } from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { ProgramDetailModal } from '../components/ui/ProgramDetailModal';
import { CheckIcon, DumbbellIcon, UsersIcon, TrendingUpIcon, HeartIcon, AwardIcon, DollarSignIcon, CalendarIcon, MapPinIcon, StarIcon, VideoIcon, BuildingIcon, GlobeIcon, ClipboardCheckIcon, BookOpenIcon, HandshakeIcon, SparklesIcon, XIcon } from 'lucide-react';
export function TrainerPartnerProgram() {
  const [activePersona, setActivePersona] = useState('inperson');
  const [expandedProgram, setExpandedProgram] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [applicationData, setApplicationData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    businessType: '',
    clientBase: '',
    website: '',
    instagram: '',
    experience: '',
    message: ''
  });
  const handleApplicationChange = e => {
    const {
      name,
      value
    } = e.target;
    setApplicationData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleApplicationSubmit = e => {
    e.preventDefault();
    // Form submission logic would go here
    alert('Thank you for your application! We will review it and contact you within 48 hours.');
    setShowApplicationForm(false);
    setApplicationData({
      name: '',
      email: '',
      phone: '',
      businessName: '',
      businessType: '',
      clientBase: '',
      website: '',
      instagram: '',
      experience: '',
      message: ''
    });
  };
  const personas = [{
    id: 'inperson',
    title: 'In-Person Trainers',
    icon: <DumbbellIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1702" />,
    description: 'Personal trainers and independent fitness coaches'
  }, {
    id: 'virtual',
    title: 'Virtual Trainers',
    icon: <VideoIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1703" />,
    description: 'Online coaches and digital fitness professionals'
  }, {
    id: 'influencers',
    title: 'Wellness Influencers',
    icon: <SparklesIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1704" />,
    description: 'Content creators and wellness advocates'
  }, {
    id: 'clubs',
    title: 'Health Clubs',
    icon: <BuildingIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1705" />,
    description: 'Gyms, studios, and fitness facilities'
  }];
  const personaContent = {
    inperson: {
      title: 'In-Person Personal Trainers',
      subtitle: 'Elevate Your Training Business with Destination Experiences',
      benefits: [{
        title: 'Increase Client Lifetime Value',
        description: 'Transform one-time training sessions into high-value retreat experiences that boost retention and referrals.',
        icon: <TrendingUpIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1706" />
      }, {
        title: 'Build Your Premium Brand',
        description: 'Position yourself as a premium trainer who offers transformative experiences, not just workout sessions.',
        icon: <AwardIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1707" />
      }, {
        title: 'Create Group Training Opportunities',
        description: 'Bring multiple clients together for group retreats, increasing your efficiency and earning potential.',
        icon: <UsersIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1708" />
      }, {
        title: 'Monetize your Passion Further',
        description: 'Create a new revenue stream within the same space and same clientele, without diverting from your career of interest.',
        icon: <BookOpenIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1709" />
      }],
      challenges: ['Limited earning potential with hourly training', 'Client retention and engagement challenges', 'Competing with big-box gym offerings', 'Seasonal business fluctuations'],
      solutions: ['High-value retreat packages', 'Exclusive client experiences', 'Premium positioning opportunity', 'Year-round booking opportunities']
    },
    virtual: {
      title: 'Virtual Trainers & Online Coaches',
      subtitle: 'Transform Your Digital Community into Real-World Results',
      benefits: [{
        title: 'Deepen Client Relationships',
        description: 'Take your virtual coaching relationship offline with immersive wellness experiences that strengthen bonds and increase retention.',
        icon: <HeartIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1710" />
      }, {
        title: 'Differentiate Your Offering',
        description: 'Stand out in the crowded online fitness space by offering exclusive retreat experiences your competitors cannot match.',
        icon: <StarIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1711" />
      }, {
        title: 'Generate Additional Revenue',
        description: 'Earn competitive commissions on every client booking while providing exceptional value to your community.',
        icon: <DollarSignIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1712" />
      }, {
        title: 'Zero Planning Burden',
        description: 'We handle all logistics, programming, and execution. You focus on your clients while we deliver the experience.',
        icon: <ClipboardCheckIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1713" />
      }],
      challenges: ['Building deeper connections with virtual-only clients', 'Differentiating from other online trainers', 'Creating memorable experiences beyond the screen', 'Generating passive income streams'],
      solutions: ['Exclusive in-person retreat experiences', 'Unique value proposition for your brand', 'Turnkey program management', 'Competitive revenue structure']
    },
    influencers: {
      title: 'Wellness Influencers & Content Creators',
      subtitle: 'Monetize Your Influence with Exclusive Wellness Experiences',
      benefits: [{
        title: 'Authentic Brand Partnerships',
        description: 'Offer your audience genuine, transformative experiences that align with your wellness values and strengthen community trust.',
        icon: <HeartIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1714" />
      }, {
        title: 'Premium Content Opportunities',
        description: 'Create compelling content from stunning locations while providing real value to your followers through exclusive retreat experiences.',
        icon: <SparklesIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1715" />
      }, {
        title: 'Diversified Revenue Streams',
        description: 'Generate income beyond sponsorships and ads with high-ticket retreat offerings and attractive commission structures.',
        icon: <DollarSignIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1716" />
      }, {
        title: 'Community Building',
        description: 'Bring your online community together for in-person connections that deepen engagement and loyalty to your brand.',
        icon: <UsersIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1717" />
      }],
      challenges: ['Monetizing influence beyond traditional sponsorships', 'Creating meaningful connections with followers', 'Standing out in saturated wellness space', 'Balancing content creation with business growth'],
      solutions: ['High-value experiential offerings', 'In-person community experiences', 'Exclusive partnership opportunities', 'Turnkey retreat management']
    },
    clubs: {
      title: 'Health Clubs & Fitness Studios',
      subtitle: 'Enhance Member Value with Exclusive Wellness Retreats',
      benefits: [{
        title: 'Member Retention Tool',
        description: 'Offer exclusive retreat experiences that increase member loyalty and reduce churn rates.',
        icon: <HeartIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1718" />
      }, {
        title: 'Premium Membership Tier',
        description: 'Create a high-value membership tier that includes access to annual wellness retreats.',
        icon: <StarIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1719" />
      }, {
        title: 'Staff Incentive Program',
        description: 'Reward top-performing trainers and staff with complimentary retreat attendance.',
        icon: <AwardIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1720" />
      }, {
        title: 'Corporate Partnership Opportunities',
        description: 'Package retreats as corporate wellness offerings for your business clients.',
        icon: <HandshakeIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1721" />
      }],
      challenges: ['Member retention in competitive markets', 'Differentiating from other local clubs', 'Maximizing membership revenue', 'Staff motivation and development'],
      solutions: ['Exclusive member benefits', 'Unique value proposition', 'Additional revenue streams', 'Staff recognition programs']
    }
  };
  // Featured programs from ComebackPrograms
  const programs = [{
    id: 'boxing-bootcamp',
    title: 'Boxing & Fitness Bootcamp with Chris Clements',
    category: 'Fitness',
    ideal: 'Athletes, fitness enthusiasts, boxing beginners',
    description: 'Train like a champion with professional boxer Chris Clements in an intensive boxing and fitness bootcamp designed to push your limits and transform your mindset.',
    duration: '4 days, 3 nights',
    price: 'Starting at $1,599',
    priceValue: 1599,
    groupSize: '15-25 participants',
    dates: ['Jan 26-29, 2026'],
    location: 'Hard Rock Hotel Cancun, Mexico',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9avHThGYydRCkXZU4SoNUQ/Boxing.webp',
    hostImage: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9jNa6v9iwd3jG5mHFF8SXo/Chris2.jpg',
    hostName: 'Chris Clements',
    hostBio: 'Professional boxer and fitness coach with over 15 years of experience training athletes at all levels.',
    highlights: ['Daily boxing training sessions with pro fighters & elite coaches', 'High-intensity interval training (HIIT) and fight-conditioning workouts', 'Mobility, recovery, and breathwork sessions to keep you performing', 'Fighter mindset & "Fight IQ" coaching (film breakdowns, strategy, psychology)', 'All-inclusive Hotel accommodations (double occupancy)', 'Small-group pods for community, accountability, and friendly competition', 'Group sparring / technical drills and padwork refinement (level-based & optional)', 'Professional photo/video content capture of your training experience', 'Airport transfers included', 'Flights handled by participants – use your airline points and travel in style']
  }, {
    id: 'wellness-reset',
    title: 'Wellness Reset: Yoga & Breathwork',
    category: 'Wellness',
    ideal: 'Wellness seekers, yoga practitioners, stress relief',
    description: 'Rejuvenate your mind, body, and spirit with a transformative wellness retreat focused on yoga and breathwork practices.',
    duration: '4 days, 3 nights',
    price: 'Starting at $1,599',
    priceValue: 1599,
    groupSize: '15-20 participants',
    dates: ['End of February 2026', 'End of April 2026'],
    location: 'Cancun/Riviera Maya',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/o1fjDw612HCk6Xa8RMhNyh/Yoga.webp',
    hostImage: 'https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=987&q=80',
    hostName: 'Maria Rodriguez',
    hostBio: 'Certified yoga instructor and breathwork specialist with 10+ years of experience guiding transformative wellness retreats.',
    highlights: ['Twice-daily yoga practices (flow, Yin/Restorative, and gentle embodied movement)', 'Guided breathwork sessions for calming, releasing, and resetting your nervous system', 'Nervous system education: stress, regulation, and everyday tools you can use at home', 'Intention-setting, reflection circles, and Soul Pods for deeper community connection', 'All-inclusive Hard Rock Hotel accommodations (double occupancy)', 'Sunrise or sunset practices in breathtaking resort settings', 'Post-retreat digital bundle with recorded practices & integration tools', 'Airport transfers included', 'Flights handled by participants – use your airline points and travel in style']
  }, {
    id: 'nutrition-wellness',
    title: 'Nutrition & Wellness Transformation',
    category: 'Wellness',
    ideal: 'Health-conscious individuals, nutrition enthusiasts',
    description: 'Transform your relationship with food and discover sustainable nutrition strategies with expert guidance in a stunning beach setting.',
    duration: '4 days, 3 nights',
    price: 'Starting at $1,599',
    priceValue: 1599,
    groupSize: '15-20 participants',
    dates: ['March 8-11, 2026', 'Sept 20-23, 2026', 'Nov 15-18, 2026'],
    location: 'Tulum, Mexico',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/aEgUSDVN3SaRCTgZHE6TX2/Nutrition.webp',
    hostImage: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
    hostName: 'Dr. Sarah Chen',
    hostBio: 'Registered dietitian and certified nutrition specialist with expertise in sustainable lifestyle transformation and mindful eating practices.',
    highlights: ['Daily workshops on metabolism, hormones, cravings, and sustainable fat loss', 'Guided meals showing you exactly how to build plates for energy and weight loss', 'Emotional and habit-based eating sessions to tackle stress and late-night snacking', 'Light daily movement sessions (walks, low-impact workouts, mobility)', 'All-inclusive Hard Rock Hotel accommodations (double occupancy)', 'Accountability Pods for support, check-ins, and real-life strategy sharing', 'Personalized 6-week post-retreat nutrition & habit blueprint', 'Airport transfers included', 'Flights handled by participants – use your airline points and travel in style']
  }];
  const commissionStructure = [{
    tier: 'Bronze Partner',
    bookings: 'up to 10 clients/year',
    commission: 'up to $100/client',
    benefits: ['New Income Opportunity for your facility', 'Brand Awareness', 'Increase in Client retention', 'Marketing Materials', 'Partner Portal access', 'Recruitment education sessions']
  }, {
    tier: 'Silver Partner',
    bookings: 'up to 25 clients/year',
    commission: 'up to $125/client',
    benefits: ['New Income Opportunity for your facility', 'Brand Awareness', 'Increase in Client retention', 'Co-branded Marketing Materials', 'Partner Portal access', 'Recruitment education sessions', 'Dedicated Account Manager']
  }, {
    tier: 'Gold Partner',
    bookings: '25+ clients/year',
    commission: 'up to $150/client + additional perks',
    benefits: ['New Income Opportunity for your facility', 'Brand Awareness', 'Increase in Client retention', 'Co-branded Marketing Materials', 'Partner Portal access', 'Recruitment education sessions', 'Dedicated Account Manager', 'Complimentary Hotel Stay for 3 nights']
  }];
  const steps = [{
    number: '1',
    title: 'Apply',
    description: 'Submit your application with your training credentials and client base information.'
  }, {
    number: '2',
    title: 'Review',
    description: 'Our team reviews your application and schedules a discovery call within 48 hours.'
  }, {
    number: '3',
    title: 'Onboard',
    description: 'Complete partner training and receive your marketing materials and booking portal access.'
  }, {
    number: '4',
    title: 'Launch',
    description: 'Start promoting programs to your clients and earn commissions on every booking.'
  }];
  const handleProgramToggle = programId => {
    setExpandedProgram(expandedProgram === programId ? null : programId);
  };
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-1722">
      {/* Hero */}
      <section className="relative py-32 md:py-40 overflow-hidden" data-id="element-1723">
        <div className="absolute inset-0 z-0" data-id="element-1724">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/1bXGSRJ9W7VoLhVjbdhEWf/HIIT.webp" alt="People exercising on a Cancun beach" className="w-full h-full object-cover" data-id="element-1725" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0099A1]/90 to-[#374047]/70" data-id="element-1726"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1727">
          <div className="max-w-3xl" data-id="element-1728">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 drop-shadow-md" data-id="element-1729">
              Trainer Partner Program
            </h1>
            <p className="text-xl text-white/90 mb-8 drop-shadow-md" data-id="element-1730">
              Transform your training business by offering exclusive wellness
              retreats in Cancun. Earn competitive commissions while providing
              unforgettable experiences to your clients.
            </p>
            <div className="flex flex-wrap gap-4" data-id="element-1731">
              <Button variant="primary" size="lg" href="https://form.typeform.com/to/H1Cr2cAj" target="_blank" rel="noopener noreferrer" data-id="element-1732">
                Apply to Partner
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form Modal */}
      {showApplicationForm && <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" data-id="element-1733">
          <div className="bg-white rounded-lg max-w-2xl w-full my-8" data-id="element-1734">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between" data-id="element-1735">
              <h2 className="text-2xl font-bold text-[#374047]" data-id="element-1736">
                Partner Application
              </h2>
              <button onClick={() => setShowApplicationForm(false)} className="text-gray-400 hover:text-gray-600" data-id="element-1737">
                <XIcon className="h-6 w-6" data-id="element-1738" />
              </button>
            </div>
            <form onSubmit={handleApplicationSubmit} className="p-6" data-id="element-1739">
              <div className="space-y-4" data-id="element-1740">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1741">
                  <div data-id="element-1742">
                    <label htmlFor="name" className="block text-sm font-medium text-[#374047] mb-1" data-id="element-1743">
                      Full Name *
                    </label>
                    <input type="text" id="name" name="name" value={applicationData.name} onChange={handleApplicationChange} required className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-1744" />
                  </div>
                  <div data-id="element-1745">
                    <label htmlFor="email" className="block text-sm font-medium text-[#374047] mb-1" data-id="element-1746">
                      Email Address *
                    </label>
                    <input type="email" id="email" name="email" value={applicationData.email} onChange={handleApplicationChange} required className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-1747" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1748">
                  <div data-id="element-1749">
                    <label htmlFor="phone" className="block text-sm font-medium text-[#374047] mb-1" data-id="element-1750">
                      Phone Number *
                    </label>
                    <input type="tel" id="phone" name="phone" value={applicationData.phone} onChange={handleApplicationChange} required className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-1751" />
                  </div>
                  <div data-id="element-1752">
                    <label htmlFor="businessName" className="block text-sm font-medium text-[#374047] mb-1" data-id="element-1753">
                      Business/Brand Name *
                    </label>
                    <input type="text" id="businessName" name="businessName" value={applicationData.businessName} onChange={handleApplicationChange} required className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-1754" />
                  </div>
                </div>
                <div data-id="element-1755">
                  <label htmlFor="businessType" className="block text-sm font-medium text-[#374047] mb-1" data-id="element-1756">
                    Business Type *
                  </label>
                  <select id="businessType" name="businessType" value={applicationData.businessType} onChange={handleApplicationChange} required className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-1757">
                    <option value="" data-id="element-1758">Select Business Type</option>
                    <option value="inperson" data-id="element-1759">In-Person Trainer</option>
                    <option value="virtual" data-id="element-1760">Virtual Trainer</option>
                    <option value="influencer" data-id="element-1761">Wellness Influencer</option>
                    <option value="club" data-id="element-1762">Health Club/Studio</option>
                  </select>
                </div>
                <div data-id="element-1763">
                  <label htmlFor="clientBase" className="block text-sm font-medium text-[#374047] mb-1" data-id="element-1764">
                    Estimated Client Base Size *
                  </label>
                  <select id="clientBase" name="clientBase" value={applicationData.clientBase} onChange={handleApplicationChange} required className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-1765">
                    <option value="" data-id="element-1766">Select Client Base Size</option>
                    <option value="small" data-id="element-1767">1-50 clients</option>
                    <option value="medium" data-id="element-1768">51-200 clients</option>
                    <option value="large" data-id="element-1769">201-500 clients</option>
                    <option value="xlarge" data-id="element-1770">500+ clients</option>
                  </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1771">
                  <div data-id="element-1772">
                    <label htmlFor="website" className="block text-sm font-medium text-[#374047] mb-1" data-id="element-1773">
                      Website URL
                    </label>
                    <input type="url" id="website" name="website" value={applicationData.website} onChange={handleApplicationChange} placeholder="https://" className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-1774" />
                  </div>
                  <div data-id="element-1775">
                    <label htmlFor="instagram" className="block text-sm font-medium text-[#374047] mb-1" data-id="element-1776">
                      Instagram Handle
                    </label>
                    <input type="text" id="instagram" name="instagram" value={applicationData.instagram} onChange={handleApplicationChange} placeholder="@yourusername" className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-1777" />
                  </div>
                </div>
                <div data-id="element-1778">
                  <label htmlFor="experience" className="block text-sm font-medium text-[#374047] mb-1" data-id="element-1779">
                    Years of Experience *
                  </label>
                  <select id="experience" name="experience" value={applicationData.experience} onChange={handleApplicationChange} required className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-1780">
                    <option value="" data-id="element-1781">Select Experience Level</option>
                    <option value="1-2" data-id="element-1782">1-2 years</option>
                    <option value="3-5" data-id="element-1783">3-5 years</option>
                    <option value="6-10" data-id="element-1784">6-10 years</option>
                    <option value="10+" data-id="element-1785">10+ years</option>
                  </select>
                </div>
                <div data-id="element-1786">
                  <label htmlFor="message" className="block text-sm font-medium text-[#374047] mb-1" data-id="element-1787">
                    Why do you want to partner with DestaMeet?
                  </label>
                  <textarea id="message" name="message" value={applicationData.message} onChange={handleApplicationChange} rows={4} className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-1788"></textarea>
                </div>
              </div>
              <div className="mt-6 flex gap-4" data-id="element-1789">
                <Button type="submit" variant="primary" className="flex-1" data-id="element-1790">
                  Submit Application
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowApplicationForm(false)} className="flex-1" data-id="element-1791">
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>}

      {/* Persona Selector */}
      <section className="py-16 bg-white" data-id="element-1792">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1793">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1794">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1795">
              Choose Your Partner Type
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-1796">
              Select your role to see how the Trainer Partner Program works for
              you
            </p>
          </div>
          {/* Persona Tabs */}
          <div className="flex justify-center mb-12 overflow-x-auto" data-id="element-1797">
            <div className="inline-flex space-x-4 bg-[#F4E8D0]/30 p-2 rounded-lg" data-id="element-1798">
              {personas.map(persona => <button key={persona.id} onClick={() => setActivePersona(persona.id)} className={`flex flex-col items-center px-6 py-4 rounded-lg transition-colors ${activePersona === persona.id ? 'bg-[#0099A1] text-white' : 'text-[#374047] hover:bg-[#0099A1]/10'}`} data-id="element-1799">
                  <div className={`mb-2 ${activePersona === persona.id ? 'text-white' : ''}`} data-id="element-1800">
                    {persona.icon}
                  </div>
                  <span className="font-medium whitespace-nowrap" data-id="element-1801">
                    {persona.title}
                  </span>
                  <span className="text-xs mt-1 opacity-80" data-id="element-1802">
                    {persona.description}
                  </span>
                </button>)}
            </div>
          </div>
          {/* Active Persona Content */}
          <div className="max-w-5xl mx-auto" data-id="element-1803">
            <div className="text-center mb-12" data-id="element-1804">
              <h3 className="text-2xl md:text-3xl font-bold text-[#374047] mb-2" data-id="element-1805">
                {personaContent[activePersona].title}
              </h3>
              <p className="text-lg text-[#374047]/80" data-id="element-1806">
                {personaContent[activePersona].subtitle}
              </p>
            </div>
            {/* Benefits Grid */}
            <div className="grid md:grid-cols-2 gap-6 mb-12" data-id="element-1807">
              {personaContent[activePersona].benefits.map((benefit, index) => <Card key={index} data-id="element-1808">
                  <CardBody className="p-6" data-id="element-1809">
                    <div className="flex items-start" data-id="element-1810">
                      <div className="flex-shrink-0 mr-4" data-id="element-1811">{benefit.icon}</div>
                      <div data-id="element-1812">
                        <h4 className="text-lg font-bold text-[#374047] mb-2" data-id="element-1813">
                          {benefit.title}
                        </h4>
                        <p className="text-[#374047]/80" data-id="element-1814">
                          {benefit.description}
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>)}
            </div>
            {/* Challenges vs Solutions */}
            <div className="grid md:grid-cols-2 gap-8" data-id="element-1815">
              <div data-id="element-1816">
                <h4 className="text-xl font-bold text-[#374047] mb-4" data-id="element-1817">
                  Common Challenges
                </h4>
                <div className="space-y-3" data-id="element-1818">
                  {personaContent[activePersona].challenges.map((challenge, index) => <div key={index} className="flex items-start bg-[#F4E8D0]/30 p-3 rounded-lg" data-id="element-1819">
                        <span className="text-[#FF6B65] mr-3" data-id="element-1820">✗</span>
                        <span className="text-[#374047]" data-id="element-1821">{challenge}</span>
                      </div>)}
                </div>
              </div>
              <div data-id="element-1822">
                <h4 className="text-xl font-bold text-[#374047] mb-4" data-id="element-1823">
                  Our Solutions
                </h4>
                <div className="space-y-3" data-id="element-1824">
                  {personaContent[activePersona].solutions.map((solution, index) => <div key={index} className="flex items-start bg-[#0099A1]/10 p-3 rounded-lg" data-id="element-1825">
                        <CheckIcon className="h-5 w-5 text-[#0099A1] mr-3 flex-shrink-0 mt-0.5" data-id="element-1826" />
                        <span className="text-[#374047]" data-id="element-1827">{solution}</span>
                      </div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Featured Programs */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-1828">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1829">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1830">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1831">
              Featured Wellness Programs
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-1832">Vacation with Intention</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-1833">
            {programs.map((program, index) => <Card key={index} className="overflow-hidden" data-id="element-1834">
                <div className="h-64 overflow-hidden" data-id="element-1835">
                  <img src={program.image} alt={program.title} className="w-full h-full object-cover" data-id="element-1836" />
                </div>
                <CardBody className="p-6" data-id="element-1837">
                  <div className="mb-2" data-id="element-1838">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0099A1]/10 text-[#0099A1]" data-id="element-1839">
                      {program.category}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-[#374047] mb-2" data-id="element-1840">
                    {program.title}
                  </h3>
                  <div className="space-y-2 mb-4" data-id="element-1841">
                    <div className="flex items-center text-sm text-[#374047]/70" data-id="element-1842">
                      <CalendarIcon className="h-4 w-4 mr-2 text-[#0099A1]" data-id="element-1843" />
                      {program.duration}
                    </div>
                    <div className="flex items-center text-sm text-[#374047]/70" data-id="element-1844">
                      <UsersIcon className="h-4 w-4 mr-2 text-[#0099A1]" data-id="element-1845" />
                      {program.ideal}
                    </div>
                    <div className="flex items-center text-sm text-[#374047]/70" data-id="element-1846">
                      <DollarSignIcon className="h-4 w-4 mr-2 text-[#0099A1]" data-id="element-1847" />
                      {program.price}
                    </div>
                  </div>
                  {/* Expandable Details */}
                  {expandedProgram === program.id && <div className="mt-4 pt-4 border-t border-[#374047]/10 space-y-4" data-id="element-1848">
                      <div data-id="element-1849">
                        <p className="text-sm text-[#374047]/80 mb-4" data-id="element-1850">
                          {program.description}
                        </p>
                      </div>
                      <div data-id="element-1851">
                        <h4 className="text-sm font-semibold text-[#374047] mb-2" data-id="element-1852">
                          Location
                        </h4>
                        <div className="flex items-center text-sm text-[#374047]/80" data-id="element-1853">
                          <MapPinIcon className="h-4 w-4 mr-2 text-[#0099A1]" data-id="element-1854" />
                          {program.location}
                        </div>
                      </div>
                      <div data-id="element-1855">
                        <h4 className="text-sm font-semibold text-[#374047] mb-2" data-id="element-1856">
                          Upcoming Dates
                        </h4>
                        <div className="flex flex-wrap gap-2" data-id="element-1857">
                          {program.dates.map((date, i) => <span key={i} className="text-xs bg-[#0099A1]/10 text-[#0099A1] px-2 py-1 rounded" data-id="element-1858">
                              {date}
                            </span>)}
                        </div>
                      </div>
                      <div data-id="element-1859">
                        <h4 className="text-sm font-semibold text-[#374047] mb-2" data-id="element-1860">
                          Program Highlights
                        </h4>
                        <ul className="space-y-1" data-id="element-1861">
                          {program.highlights.map((highlight, i) => <li key={i} className="flex items-start text-sm text-[#374047]/80" data-id="element-1862">
                              <CheckIcon className="h-4 w-4 text-[#0099A1] mr-2 flex-shrink-0 mt-0.5" data-id="element-1863" />
                              <span data-id="element-1864">{highlight}</span>
                            </li>)}
                        </ul>
                      </div>
                      {program.hostImage && program.hostName && <div className="bg-[#F4E8D0]/30 p-3 rounded-lg" data-id="element-1865">
                          <h4 className="text-sm font-semibold text-[#374047] mb-2" data-id="element-1866">
                            Your Host
                          </h4>
                          <div className="flex items-start" data-id="element-1867">
                            <img src={program.hostImage} alt={program.hostName} className="w-12 h-12 rounded-full object-cover mr-3 flex-shrink-0" data-id="element-1868" />
                            <div data-id="element-1869">
                              <p className="font-semibold text-sm text-[#374047]" data-id="element-1870">
                                {program.hostName}
                              </p>
                              {program.hostBio && <p className="text-xs text-[#374047]/80 mt-1" data-id="element-1871">
                                  {program.hostBio}
                                </p>}
                            </div>
                          </div>
                        </div>}
                      <Button variant="primary" className="w-full" href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ0LqS_WBaJU_UweOzewNSfk2vu7lxzFJN3nlB21e-yByPHLTO2p6nMafIxTmsxJzJbdXaw9qtoL" target="_blank" data-id="element-1872">
                        Book Appointment to Partner with Us
                      </Button>
                    </div>}
                  <Button variant="outline" className="w-full mt-4" onClick={() => handleProgramToggle(program.id)} data-id="element-1873">
                    {expandedProgram === program.id ? 'Show Less' : 'View Details'}
                  </Button>
                </CardBody>
              </Card>)}
          </div>
          <div className="text-center mt-8" data-id="element-1874">
            <Button variant="primary" href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ0LqS_WBaJU_UweOzewNSfk2vu7lxzFJN3nlB21e-yByPHLTO2p6nMafIxTmsxJzJbdXaw9qtoL" target="_blank" data-id="element-1875">
              Book Appointment to Partner with Us
            </Button>
          </div>
        </div>
      </section>
      {/* Commission Structure */}
      <section className="py-16 bg-white" data-id="element-1876">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1877">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1878">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1879">
              Competitive Commission Structure
            </h2>
            <p className="text-lg text-[#374047]/80 mb-2" data-id="element-1880">
              Earn more as you grow. Our tiered structure rewards your success.
            </p>
            <p className="text-base font-semibold text-[#0099A1]" data-id="element-1881">
              No financial investment required
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-1882">
            {commissionStructure.map((tier, index) => <Card key={index} className={`${index === 1 ? 'border-2 border-[#0099A1]' : ''}`} data-id="element-1883">
                <CardHeader className={`${index === 1 ? 'bg-[#0099A1] text-white' : 'bg-[#F4E8D0]/30'} p-6`} data-id="element-1884">
                  {index === 1 && <div className="text-center mb-2" data-id="element-1885">
                      <span className="inline-block bg-[#FF6B65] text-white text-xs px-3 py-1 rounded-full" data-id="element-1886">
                        Most Popular
                      </span>
                    </div>}
                  <h3 className={`text-2xl font-bold ${index === 1 ? 'text-white' : 'text-[#374047]'} text-center`} data-id="element-1887">
                    {tier.tier}
                  </h3>
                  <p className={`text-center ${index === 1 ? 'text-white/80' : 'text-[#374047]/70'} mt-2`} data-id="element-1888">
                    {tier.bookings}
                  </p>
                  <div className="text-center mt-4" data-id="element-1889">
                    <span className={`text-3xl font-bold ${index === 1 ? 'text-white' : 'text-[#0099A1]'}`} data-id="element-1890">
                      {tier.commission}
                    </span>
                  </div>
                </CardHeader>
                <CardBody className="p-6" data-id="element-1891">
                  <ul className="space-y-3" data-id="element-1892">
                    {tier.benefits.map((benefit, i) => <li key={i} className="flex items-start" data-id="element-1893">
                        <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5 mr-2" data-id="element-1894" />
                        <span className="text-[#374047]" data-id="element-1895">{benefit}</span>
                      </li>)}
                  </ul>
                </CardBody>
              </Card>)}
          </div>
        </div>
      </section>
      {/* How It Works */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-1896">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1897">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1898">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1899">
              How It Works
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-1900">
              Get started in four simple steps
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto" data-id="element-1901">
            {steps.map((step, index) => <div key={index} className="text-center" data-id="element-1902">
                <div className="w-16 h-16 bg-[#0099A1] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4" data-id="element-1903">
                  {step.number}
                </div>
                <h3 className="text-xl font-bold text-[#374047] mb-2" data-id="element-1904">
                  {step.title}
                </h3>
                <p className="text-[#374047]/80" data-id="element-1905">{step.description}</p>
              </div>)}
          </div>
        </div>
      </section>
      {/* Partner Benefits */}
      <section className="py-16 bg-white" data-id="element-1906">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1907">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1908">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1909">
              Why Partner With DestaMeet?
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-1910">
            <div className="text-center" data-id="element-1911">
              <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1912">
                <ClipboardCheckIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1913" />
              </div>
              <h3 className="text-lg font-bold text-[#374047] mb-2" data-id="element-1914">
                Turnkey Operations
              </h3>
              <p className="text-[#374047]/80" data-id="element-1915">
                We handle all logistics, programming, and execution. You focus
                on your clients.
              </p>
            </div>
            <div className="text-center" data-id="element-1916">
              <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1917">
                <DollarSignIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1918" />
              </div>
              <h3 className="text-lg font-bold text-[#374047] mb-2" data-id="element-1919">
                Competitive Commissions
              </h3>
              <p className="text-[#374047]/80" data-id="element-1920">
                Earn up to 20% commission on all bookings with our tiered
                structure.
              </p>
            </div>
            <div className="text-center" data-id="element-1921">
              <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1922">
                <StarIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1923" />
              </div>
              <h3 className="text-lg font-bold text-[#374047] mb-2" data-id="element-1924">
                Premium Experiences
              </h3>
              <p className="text-[#374047]/80" data-id="element-1925">
                Offer world-class wellness programs at luxury resorts in Cancun.
              </p>
            </div>
            <div className="text-center" data-id="element-1926">
              <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1927">
                <HandshakeIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1928" />
              </div>
              <h3 className="text-lg font-bold text-[#374047] mb-2" data-id="element-1929">
                Dedicated Support
              </h3>
              <p className="text-[#374047]/80" data-id="element-1930">
                Your own account manager to help you succeed every step of the
                way.
              </p>
            </div>
            <div className="text-center" data-id="element-1931">
              <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1932">
                <BookOpenIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1933" />
              </div>
              <h3 className="text-lg font-bold text-[#374047] mb-2" data-id="element-1934">
                Marketing Resources
              </h3>
              <p className="text-[#374047]/80" data-id="element-1935">
                Professional marketing materials and co-branded content to
                promote programs.
              </p>
            </div>
            <div className="text-center" data-id="element-1936">
              <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1937">
                <GlobeIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1938" />
              </div>
              <h3 className="text-lg font-bold text-[#374047] mb-2" data-id="element-1939">
                Partner Portal
              </h3>
              <p className="text-[#374047]/80" data-id="element-1940">
                Easy-to-use booking system and dashboard to track your
                commissions.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-16 bg-[#0099A1]" data-id="element-1941">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1942">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1943">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1944">
              Ready to Transform Your Training Business?
            </h2>
            <p className="text-xl text-white/90 mb-8" data-id="element-1945">
              Join our network of elite fitness professionals and start offering
              unforgettable wellness experiences to your clients.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1946">
              <Button variant="primary" size="lg" href="https://form.typeform.com/to/H1Cr2cAj" target="_blank" rel="noopener noreferrer" data-id="element-1947">
                Apply Now
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10" href="https://calendar.app.google/4vusYaEgW5mhzbSV9" target="_blank" data-id="element-1948">
                Schedule a Call
              </Button>
            </div>
            <p className="text-sm text-white/70 mt-6" data-id="element-1949">
              Applications reviewed within 48 hours
            </p>
          </div>
        </div>
      </section>
      {/* Program Detail Modal */}
      {selectedProgram && <ProgramDetailModal program={selectedProgram} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} data-id="element-1950" />}
    </div>;
}